import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { EncryptedBlob, MessageView, OutboxEvent } from "@baton/contracts";
import { ApiError, type DashboardApi } from "../api.js";
import type { CryptoWorkerClient } from "../crypto-client.js";
import type { DecryptedMessage, MessageContent } from "../messages.js";
import type { ThreadMessage } from "../chat/thread.js";

const MESSAGE_CAP = 500;

export interface SendDraft {
  recipientAgentIds: string[];
  type: "chat" | "handoff";
  content: MessageContent;
  replyToMessageId?: string | null;
  repoId?: string | null;
}

export interface SendContext {
  sessionId: string;
  senderAgentId: string;
}

/**
 * Project-scoped messages, decrypted in the worker and merged incrementally: a realtime
 * `message.*` event refetches just that message (bursts coalesce), and a send inserts an
 * optimistic entry that the API response replaces. Plaintext lives in this hook's state
 * and nowhere else.
 */
export function useProjectMessages(api: DashboardApi, worker: CryptoWorkerClient, projectId: string | null) {
  const [byId, setById] = useState<Map<string, ThreadMessage>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const inflight = useRef(new Map<string, { dirty: boolean }>());
  const sequence = useRef(0);

  const merge = useCallback((incoming: ThreadMessage[]) => {
    setById((current) => {
      const next = new Map(current);
      for (const message of incoming) next.set(message.id, message);
      if (next.size > MESSAGE_CAP) {
        const evictable = [...next.values()].filter((message) => !message.pending)
          .sort((left, right) => left.createdAt.localeCompare(right.createdAt));
        for (const message of evictable.slice(0, next.size - MESSAGE_CAP)) next.delete(message.id);
      }
      return next;
    });
  }, []);

  const load = useCallback(async () => {
    if (!projectId) { setById(new Map()); return; }
    const request = ++sequence.current;
    try {
      const raw = await api.feed({ projectId, limit: 200 });
      const decrypted = await worker.call<DecryptedMessage[]>("decryptMessages", { messages: raw });
      if (request !== sequence.current) return;
      setById(new Map(decrypted.map((message) => [message.id, message])));
      setError(null);
    } catch (caught) {
      if (request === sequence.current) setError(caught instanceof Error ? caught.message : "Could not load messages");
    }
  }, [api, worker, projectId]);

  useEffect(() => { inflight.current.clear(); void load(); }, [load]);

  const refetchOne = useCallback(async (messageId: string) => {
    const state = inflight.current.get(messageId);
    if (state) { state.dirty = true; return; }
    inflight.current.set(messageId, { dirty: false });
    try {
      for (;;) {
        let raw: MessageView;
        try {
          raw = await api.message(messageId);
        } catch (caught) {
          if (caught instanceof ApiError && caught.status === 404) return;
          throw caught;
        }
        const [decrypted] = await worker.call<DecryptedMessage[]>("decryptMessages", { messages: [raw] });
        if (decrypted && decrypted.projectId === projectId) merge([decrypted]);
        const current = inflight.current.get(messageId);
        if (!current?.dirty) break;
        current.dirty = false;
      }
    } finally {
      inflight.current.delete(messageId);
    }
  }, [api, worker, projectId, merge]);

  /** Routes a realtime outbox event; returns the message id when it belonged to this project. */
  const onEvent = useCallback((event: OutboxEvent): boolean => {
    if (!event.topic.startsWith("message.") || !projectId) return false;
    const payload = event.payload as { messageId?: string; projectId?: string | null };
    const messageId = payload.messageId ?? event.aggregateId;
    if (payload.projectId !== projectId) return false;
    void refetchOne(messageId).catch(() => undefined);
    return true;
  }, [projectId, refetchOne]);

  const send = useCallback(async (context: SendContext, draft: SendDraft): Promise<ThreadMessage> => {
    if (!projectId) throw new Error("Select a project before sending");
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    const optimistic: ThreadMessage = {
      id,
      senderAgentId: context.senderAgentId,
      recipientAgentIds: draft.recipientAgentIds,
      type: draft.type,
      visibility: "participants",
      attachmentIds: [],
      replyToMessageId: draft.replyToMessageId ?? null,
      repoId: draft.repoId ?? null,
      projectId,
      expiresAt: null,
      createdAt: now,
      content: draft.content,
      recipientStates: draft.recipientAgentIds.map((agentId) => ({
        agentId, state: "queued", result: null, openedAt: null, acknowledgedAt: null, completedAt: null,
      })),
      pending: true,
    };
    merge([optimistic]);
    try {
      const ciphertext = await worker.call<EncryptedBlob>("encryptMessage", { id, type: draft.type, content: draft.content });
      const sent = await api.sendMessage({
        id,
        recipientAgentIds: draft.recipientAgentIds,
        type: draft.type,
        visibility: "participants",
        ciphertext,
        replyToMessageId: draft.replyToMessageId ?? null,
        repoId: draft.repoId ?? null,
        projectId,
        idempotencyKey: crypto.randomUUID(),
      });
      const [decrypted] = await worker.call<DecryptedMessage[]>("decryptMessages", { messages: [sent] });
      const confirmed: ThreadMessage = decrypted ?? { ...optimistic, pending: false };
      merge([confirmed]);
      return confirmed;
    } catch (caught) {
      merge([{ ...optimistic, pending: false, failed: true }]);
      throw caught;
    }
  }, [api, worker, projectId, merge]);

  const messages = useMemo(() => [...byId.values()], [byId]);
  return { messages, error, reload: load, onEvent, send };
}
